import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { BrokeredSystemNotice, CapabilityChips, CredentialReference } from '@/components/platform/CapabilityChips';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import { IntegrationsArea } from './IntegrationsArea';
import {
  CONNECTION_AUTHENTICATION_TYPES,
  DEFAULT_CAPABILITIES,
  INTEGRATION_CAPABILITIES,
  INTEGRATION_ENVIRONMENTS,
  INTEGRATION_SYSTEM_CODES,
  isVaultReference,
  type ConnectionAuthenticationType,
  type IntegrationCapability,
  type IntegrationEnvironment,
  type IntegrationSystemCode,
  type PlatformConnection,
  type RegisterPlatformConnectionRequest,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * The shared service connections, one row per system per environment.
 *
 * NO INSTITUTION IS CHOSEN TO REACH THIS SCREEN, and that is the change. These
 * rows describe where CampusOne Core sends traffic and how it authenticates —
 * facts that are true of the whole environment and identical for every tenant. The
 * previous page could only be entered by typing an institution id, which made
 * "is the Digital Switch healthy?" a question whose answer depended on which
 * school you happened to pick.
 *
 * NO FIELD ON THIS SCREEN ACCEPTS A SECRET. The registration form takes a vault
 * POINTER and validates it in the browser before anything is sent, so a value that
 * is not a pointer — which is to say, quite likely an actual key — never leaves
 * the machine, never reaches a request log and never sits in form state that a
 * crash reporter might serialise. The server enforces the same rule; this is the
 * shorter blast radius, not a replacement for it.
 */

export function PlatformConnectionsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const [registerOpen, setRegisterOpen] = useState(false);

  const environment = readEnvironment(params.get('environment'));

  const fetcher = useCallback(
    () => platformApi.connections.list(environment ? { environment } : {}),
    [environment],
  );
  const { state, reload } = useAsyncData<PlatformConnection[]>(
    `connections:${environment ?? 'ALL'}`,
    fetcher,
  );

  return (
    <IntegrationsArea
      active="connections"
      title={t('platform.connections.title')}
      lead={t('platform.connections.lead')}
      actions={
        <button type="button" className={styles.primaryBtn} onClick={() => setRegisterOpen(true)}>
          <Icon name="plus" size={16} />
          {t('platform.connections.register')}
        </button>
      }
    >
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.connections.secretsNotice')}
      </p>

      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="connections-environment">
            {t('platform.connections.colEnvironment')}
          </label>
          <Select
            id="connections-environment"
            value={environment ?? 'ALL'}
            onChange={(value) => setParams(value === 'ALL' ? {} : { environment: value as IntegrationEnvironment })}
            options={[
              { value: 'ALL', label: t('platform.connections.environmentAll') },
              ...INTEGRATION_ENVIRONMENTS.map((value) => ({ value, label: t(`platform.environment.${value}`) })),
            ]}
          />
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </div>

      <Panel title={t('platform.connections.tableTitle')} icon="integration">
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.connections.emptyTitle')}
          emptyBody={t('platform.connections.emptyBody')}
        >
          {(data) => (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>
                  {t('platform.connections.tableCaption', { n: data.length })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.connections.colSystem')}</th>
                    <th scope="col">{t('platform.connections.colEnvironment')}</th>
                    <th scope="col">{t('platform.connections.colStatus')}</th>
                    <th scope="col">{t('platform.connections.colAuthentication')}</th>
                    <th scope="col">{t('platform.connections.colLastCheck')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((connection) => (
                    <tr key={connection.id}>
                      <th scope="row" className={styles.tableRowHeader}>
                        <Link
                          className={styles.rowTitleLink}
                          to={`/admin/integrations/connections/${encodeURIComponent(connection.id)}`}
                        >
                          {connection.displayName}
                        </Link>
                        <span className={styles.tableSub}>
                          {t(`platform.system.${connection.systemCode}`, {
                            defaultValue: connection.systemCode,
                          })}
                        </span>
                        {/* The capability list sits in the System cell because it
                            is what this row is FOR. A separate column would have
                            been unreadable at five capabilities and would have
                            invited someone to go looking for a WhatsApp row. */}
                        <CapabilityChips
                          systemCode={connection.systemCode}
                          capabilities={connection.capabilities}
                        />
                      </th>
                      <td>{t(`platform.environment.${connection.environment}`)}</td>
                      <td>
                        <StateBadge kind="integration" value={connection.status} size="sm" />
                        {/*
                          A connection that has never once answered has not merely
                          "not been checked recently" — there is no evidence it has
                          ever worked. The server refuses to record HEALTHY without
                          it, and the table says so rather than leaving a neutral
                          grey badge to be read as "probably fine".
                        */}
                        {!connection.lastSuccessfulAt && connection.status !== 'DISABLED' && (
                          <span className={styles.tableSub}>
                            {t('platform.connections.neverSucceeded')}
                          </span>
                        )}
                      </td>
                      <td>
                        {t(`platform.authentication.${connection.authenticationType}`, {
                          defaultValue: connection.authenticationType,
                        })}
                        <span className={styles.tableSub}>
                          <CredentialReference reference={connection.credentialVaultReference} />
                        </span>
                      </td>
                      <td className={styles.mutedCell}>
                        {connection.lastCheckedAt
                          ? formatRelative(connection.lastCheckedAt, locale)
                          : t('platform.connections.neverChecked')}
                        {connection.lastSuccessfulAt && (
                          <span className={styles.tableSub}>
                            {t('platform.connections.lastSuccess', {
                              when: formatDateTime(connection.lastSuccessfulAt, locale),
                            })}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncPanel>
      </Panel>

      <RegisterConnectionDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onRegistered={() => {
          setRegisterOpen(false);
          reload();
        }}
      />
    </IntegrationsArea>
  );
}

function readEnvironment(value: string | null): IntegrationEnvironment | undefined {
  return INTEGRATION_ENVIRONMENTS.find((candidate) => candidate === value);
}

/* ─── Register ─── */

function RegisterConnectionDialog({
  open,
  onClose,
  onRegistered,
}: {
  open: boolean;
  onClose: () => void;
  onRegistered: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<RegisterPlatformConnectionRequest>(emptyConnection());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyConnection());
      setError(null);
    }
  }, [open]);

  const urlPattern = import.meta.env.DEV ? /^https?:\/\/\S+$/ : /^https:\/\/\S+$/;
  const urlValid = !form.baseUrl || urlPattern.test(form.baseUrl);
  const healthUrlValid = !form.healthCheckUrl || urlPattern.test(form.healthCheckUrl);
  // Empty is allowed. A connection may be registered before the credential exists
  // and sits in Not configured until one is referenced and a probe succeeds. What
  // is not allowed is a value that is not a pointer.
  const vaultValid = !form.credentialVaultReference || isVaultReference(form.credentialVaultReference);
  const valid = form.displayName.trim().length >= 2 && urlValid && healthUrlValid && vaultValid;

  const changeSystem = (systemCode: IntegrationSystemCode) =>
    setForm((current) => ({
      ...current,
      systemCode,
      // Re-seeded rather than preserved: capabilities left over from the previous
      // choice would claim this system does things it has never done.
      capabilities: [...DEFAULT_CAPABILITIES[systemCode]],
    }));

  const toggleCapability = (capability: IntegrationCapability) =>
    setForm((current) => ({
      ...current,
      capabilities: current.capabilities.includes(capability)
        ? current.capabilities.filter((entry) => entry !== capability)
        : [...current.capabilities, capability],
    }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.connections.register({
        systemCode: form.systemCode,
        displayName: form.displayName.trim(),
        environment: form.environment,
        baseUrl: form.baseUrl?.trim() || undefined,
        healthCheckUrl: form.healthCheckUrl?.trim() || undefined,
        apiVersion: form.apiVersion?.trim() || undefined,
        authenticationType: form.authenticationType,
        clientId: form.clientId?.trim() || undefined,
        credentialVaultReference: form.credentialVaultReference?.trim() || undefined,
        capabilities: form.capabilities,
      });
      onRegistered();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.connections.registerTitle')}
      description={t('platform.connections.registerLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.colSystem')}</span>
            <Select
              value={form.systemCode}
              onChange={(value) => changeSystem(value as IntegrationSystemCode)}
              options={INTEGRATION_SYSTEM_CODES.map((code) => ({
                value: code,
                label: t(`platform.system.${code}`, { defaultValue: code }),
              }))}
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldSystemHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.colEnvironment')}</span>
            <Select
              value={form.environment}
              onChange={(value) => setForm({ ...form, environment: value as IntegrationEnvironment })}
              options={INTEGRATION_ENVIRONMENTS.map((value) => ({
                value,
                label: t(`platform.environment.${value}`),
              }))}
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldEnvironmentHint')}</span>
          </label>
        </div>

        <BrokeredSystemNotice systemCode={form.systemCode} />

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.connections.fieldName')}</span>
          <input
            className={styles.input}
            value={form.displayName}
            required
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          />
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldBaseUrl')}</span>
            <input
              className={styles.input}
              type="url"
              value={form.baseUrl ?? ''}
              aria-invalid={(form.baseUrl ?? '').length > 0 && !urlValid}
              onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldBaseUrlHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldHealthUrl')}</span>
            <input
              className={styles.input}
              type="url"
              value={form.healthCheckUrl ?? ''}
              aria-invalid={(form.healthCheckUrl ?? '').length > 0 && !healthUrlValid}
              onChange={(event) => setForm({ ...form, healthCheckUrl: event.target.value })}
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldHealthUrlHint')}</span>
          </label>
        </div>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.colAuthentication')}</span>
            <Select
              value={form.authenticationType}
              onChange={(value) => setForm({ ...form, authenticationType: value as ConnectionAuthenticationType })}
              options={CONNECTION_AUTHENTICATION_TYPES.map((value) => ({
                value,
                label: t(`platform.authentication.${value}`),
              }))}
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldAuthHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.connections.fieldClientId')}</span>
            <input
              className={styles.input}
              value={form.clientId ?? ''}
              autoComplete="off"
              onChange={(event) => setForm({ ...form, clientId: event.target.value })}
            />
            <span className={styles.fieldHint}>{t('platform.connections.fieldClientIdHint')}</span>
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.connections.fieldApiVersion')}</span>
          <input
            className={styles.input}
            value={form.apiVersion ?? ''}
            autoComplete="off"
            onChange={(event) => setForm({ ...form, apiVersion: event.target.value })}
          />
          <span className={styles.fieldHint}>{t('platform.connections.fieldApiVersionHint')}</span>
        </label>

        <div className={styles.warningNotice} role="note">
          <Icon name="noAccess" size={16} aria-hidden="true" />
          <div>
            <p className={styles.warningTitle}>{t('platform.connections.vaultTitle')}</p>
            <p className={styles.warningBody}>{t('platform.connections.vaultBody')}</p>
          </div>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.connections.fieldVaultRef')}</span>
          <input
            className={styles.input}
            value={form.credentialVaultReference ?? ''}
            placeholder="vault://campusone/connections/digital-switch#hmac_secret"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={(form.credentialVaultReference ?? '').length > 0 && !vaultValid}
            aria-describedby="connection-vault-hint"
            onChange={(event) => setForm({ ...form, credentialVaultReference: event.target.value })}
          />
          <span className={styles.fieldHint} id="connection-vault-hint">
            {t('platform.connections.fieldVaultRefHint')}
          </span>
          {(form.credentialVaultReference ?? '').length > 0 && !vaultValid && (
            <span className={styles.fieldError} role="alert">
              {t('platform.connections.vaultInvalid')}
            </span>
          )}
        </label>

        <fieldset className={styles.fieldset}>
          <legend className={styles.fieldLabel}>{t('platform.connections.capabilitiesLabel')}</legend>
          <p className={styles.fieldHint}>{t('platform.connections.capabilitiesHint')}</p>
          <div className={styles.capabilityGrid}>
            {INTEGRATION_CAPABILITIES.map((capability) => (
              <Checkbox
                key={capability}
                label={t(`platform.capability.${capability}`, { defaultValue: capability })}
                checked={form.capabilities.includes(capability)}
                onChange={() => toggleCapability(capability)}
              />
            ))}
          </div>
        </fieldset>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.connections.register')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyConnection(): RegisterPlatformConnectionRequest {
  return {
    systemCode: 'EDUCORE',
    displayName: '',
    environment: 'PRODUCTION',
    baseUrl: '',
    healthCheckUrl: '',
    apiVersion: '',
    authenticationType: 'API_KEY',
    clientId: '',
    credentialVaultReference: '',
    capabilities: [...DEFAULT_CAPABILITIES.EDUCORE],
  };
}
